// Generación de la imagen PNG del QR compartido/guardado, con quiet zone
// (borde blanco) y fondo opaco. Paridad con Sima-appmovil
// (src/screens/CodeQR/CodeQrScreen.js): mismas constantes 1024 / 128 / "M",
// para que un lector de máquina expendedora lea igual de bien el QR
// generado desde la web que el generado desde la app móvil.
//
// Este módulo es la única fuente de esta lógica: OrderPage.jsx y
// ShareOrderModal.jsx la consumen en vez de duplicarla. Cambiar el margen o
// el tamaño acá afecta a ambas vistas.

// Lado del área de módulos del QR compartido (sin contar el margen).
export const SHARED_QR_SIZE = 1024;
// Borde blanco (quiet zone) alrededor del QR compartido. El estándar QR pide
// ~4 módulos de zona silenciosa; 128/1024 replica la proporción ya validada
// en Sima-appmovil (allí se subió de 24 a 128 porque las máquinas
// expendedoras tenían problemas de lectura con un margen tan angosto).
export const SHARED_QR_QUIET_ZONE = 128;
// Nivel de corrección de errores del QR compartido (paridad con `ecl="M"` en
// Sima-appmovil). Más tolerante a suciedad/reflejo en el lector que el
// "L" por defecto de react-qr-code.
export const SHARED_QR_ECL = "M";
// Lado total del canvas exportado: el margen se agrega alrededor del QR,
// nunca lo encoge (1024 + 2*128 = 1280).
export const SHARED_QR_TOTAL_SIZE = SHARED_QR_SIZE + 2 * SHARED_QR_QUIET_ZONE;

/**
 * Construye el nombre del archivo descargado, manteniendo el patrón
 * `qr_code_{pedido_id}_{timestamp}.png`.
 */
export function buildQrFileName(pedidoId) {
  return `qr_code_${pedidoId}_${Date.now()}.png`;
}

/**
 * Convierte un <svg> (el nodo del QR oculto, o como fallback el QR visible)
 * a un Blob PNG con quiet zone y fondo blanco opaco.
 *
 * No usa `btoa`: el SVG serializado puede contener caracteres > U+00FF
 * (btoa lanza InvalidCharacterError en ese caso), así que el data URL se
 * construye con `encodeURIComponent`, que no tiene esa limitación.
 *
 * Las dimensiones del canvas se calculan siempre desde `size`/`quietZone`,
 * nunca desde `img.width`/`img.height` (evita canvas 0x0 si el SVG
 * serializado perdiera sus atributos width/height).
 */
export function svgElementToPngBlob(
  svgElement,
  { size = SHARED_QR_SIZE, quietZone = SHARED_QR_QUIET_ZONE } = {},
) {
  return new Promise((resolve, reject) => {
    if (!svgElement) {
      reject(new Error("SVG no encontrado."));
      return;
    }

    const clone = svgElement.cloneNode(true);
    clone.setAttribute("width", String(size));
    clone.setAttribute("height", String(size));
    if (!clone.getAttribute("xmlns")) {
      clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    }

    const svgData = new XMLSerializer().serializeToString(clone);
    const dataUrl =
      "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgData);

    const total = size + 2 * quietZone;
    const canvas = document.createElement("canvas");
    canvas.width = total;
    canvas.height = total;
    const ctx = canvas.getContext("2d");

    const img = new Image();

    img.onload = () => {
      // Fondo blanco opaco en todo el lienzo (margen + huecos entre
      // módulos, que en el SVG de react-qr-code no se pintan) ANTES de
      // dibujar el QR, para que el PNG resultante no tenga alfa.
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, total, total);
      ctx.drawImage(img, quietZone, quietZone, size, size);

      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Error al generar la imagen."));
          return;
        }
        resolve(blob);
      }, "image/png");
    };

    img.onerror = () => {
      reject(new Error("Error al cargar la imagen SVG."));
    };

    img.src = dataUrl;
  });
}

/**
 * Descarga un Blob como archivo, con el mismo mecanismo (anchor +
 * URL.createObjectURL, revocado 100ms después) que ya usaba el código
 * duplicado en OrderPage.jsx / ShareOrderModal.jsx.
 */
export function downloadPngBlob(blob, filename) {
  const downloadLink = document.createElement("a");
  const url = URL.createObjectURL(blob);
  downloadLink.href = url;
  downloadLink.download = filename;

  document.body.appendChild(downloadLink);
  downloadLink.click();

  setTimeout(() => {
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Intenta copiar la imagen al portapapeles. Nunca lanza: hace
 * feature-detect de ClipboardItem/navigator.clipboard.write y espera
 * (await) el resultado real de la escritura asíncrona, en vez de dar por
 * copiado algo que todavía puede rechazar (Safari, documento sin foco,
 * permiso denegado).
 */
export async function copyPngBlobToClipboard(blob) {
  if (
    typeof ClipboardItem === "undefined" ||
    !navigator.clipboard?.write
  ) {
    return false;
  }

  try {
    const clipboardItem = new ClipboardItem({ "image/png": blob });
    await navigator.clipboard.write([clipboardItem]);
    return true;
  } catch (error) {
    console.error("Error al copiar la imagen:", error);
    return false;
  }
}
